<template>
    <div :class="customer">
        <div class="main-container">
            <div class="toolbar">
                <img class="logo" :src="logo.image" :alt="logo.alt" transition="scale-transition" height="20px" />
            </div>
            <div class="login-form">
                <v-form v-if="step === STEP_CREDENTIALS" class="flex-grow-1" autocomplete="on" @submit.prevent="login">
                    <v-container class="inner-container">
                        <v-row dense>
                            <v-col>
                                <h4>{{ $t('login') }}</h4>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col>
                                <LabelField :label="$t('username')">
                                    <v-text-field
                                        v-model="username"
                                        dense
                                        outlined
                                        :placeholder="$t('zb') + ' meierh'"
                                        autocomplete="on"
                                        :autofocus="true"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col>
                                <LabelField :label="$t('pw')">
                                    <v-text-field
                                        v-model="password"
                                        placeholder="..."
                                        outlined
                                        dense
                                        :type="showPw ? 'text' : 'password'"
                                        append-icon="ic-20 ic-preview ic-col-primary"
                                        @click:append="showPw = !showPw"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col class="text-right">
                                <ActionBtn :loading="loading" :disabled="loading" type="submit" @click.prevent="login">
                                    <KpIcon icon-cls="arrow-bigright" color="white" />
                                    <span>{{ $t('login') }}</span>
                                </ActionBtn>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col>
                                <p>
                                    <router-link :to="{ name: 'reset_pw' }"> {{ $t('forgot_pw') }} </router-link><br />
                                </p>
                                <p v-if="signupUrl">
                                    <a :href="signupUrl" rel="noopener noreferrer">{{ $t('new_free_account') }}</a>
                                </p>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col>
                                <v-fade-transition>
                                    <v-alert v-if="error" type="error">{{ error }}</v-alert>
                                </v-fade-transition>
                            </v-col>
                        </v-row>
                    </v-container>
                    <v-alert v-if="infoMsg" class="info-area" outlined type="info">
                        <div class="info-text" v-html="infoMsg"></div>
                    </v-alert>
                </v-form>

                <!--
                    Second login stage. The password was correct, but the session is NOT
                    authenticated yet: the server holds a short lived pending state and
                    answers 401 on every other route until the code is confirmed.
                -->
                <div v-else class="flex-grow-1">
                    <v-container class="inner-container totp-container">
                        <TotpSetup v-if="step === STEP_TOTP_SETUP" :qr-code="totpQrCode" :secret="totpSecret" />
                        <TotpCode
                            ref="totpCode"
                            :title="step === STEP_TOTP_CODE ? $t('totp_code_title') : ''"
                            :loading="loading"
                            :error="totpError"
                            @verify="onTotpVerify"
                            @cancel="backToCredentials()"
                        />
                    </v-container>
                </div>
                <div class="footer" v-html="loginFooterHtml"></div>
            </div>
            <div class="image-area"></div>
        </div>

        <v-dialog v-model="showPwChangeDialog" max-width="600px" persistent>
            <v-card>
                <v-card-title>{{ $t('change_pw') }}</v-card-title>
                <v-card-text>
                    <v-form ref="pwForm" v-model="pwFormValid" dense>
                        <v-text-field v-model="oldPw" type="password" :label="$t('old_pw')" dense outlined />
                        <v-text-field
                            v-model="newPw"
                            type="password"
                            :rules="getPWFieldRules()"
                            :label="$t('new_pw')"
                            dense
                            outlined
                            @input="$refs.pwForm.validate()"
                        />
                        <v-text-field
                            v-model="newPwConfirm"
                            :label="$t('confirm_pw')"
                            :rules="getPWConfirmFieldRules()"
                            type="password"
                            dense
                            outlined
                            @input="$refs.pwForm.validate()"
                        />
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <PlainBtn @click="showPwChangeDialog = false">{{ $t('cancel') }}</PlainBtn>
                    <ActionBtn :loading="loading" :disabled="!pwFormValid" @click="onChangePwBtn">{{
                        $t('absenden')
                    }}</ActionBtn>
                </v-card-actions>
                <v-fade-transition>
                    <v-card-text v-if="pwChangeError">
                        <v-alert type="error">{{ pwChangeError }}</v-alert>
                    </v-card-text>
                </v-fade-transition>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import dayjs from 'dayjs';
import { appUrl, customer, globalParam } from '@/helpers/globals';
import { formRules, errToStr } from '@/helpers/tools';
import { instance as api } from '@/helpers/api';
import qs from 'qs';
import { logo, signupUrl, loginFooterHtml } from '@/customer-specific';
import sessionStorageMixin from '@/mixins/session-storage';
import { totpSetup, totpVerify } from '@/helpers/auth';
import TotpSetup from '@/views/guest/TotpSetup.vue';
import TotpCode from '@/views/guest/TotpCode.vue';

const STEP_CREDENTIALS = 'credentials';
const STEP_TOTP_SETUP = 'totp_setup';
const STEP_TOTP_CODE = 'totp_code';

export default {
    name: 'LoginView',
    components: { TotpSetup, TotpCode },
    mixins: [sessionStorageMixin],
    data: function () {
        return {
            username: null,
            password: null,
            showPw: false,
            loading: false,
            showPwChangeDialog: false,
            oldPw: '',
            newPw: '',
            newPwConfirm: '',
            pwFormValid: false,
            pwChangeError: null,
            /** which of the three login steps is shown */
            step: STEP_CREDENTIALS,
            totpQrCode: '',
            totpSecret: '',
            totpError: null,
            STEP_CREDENTIALS,
            STEP_TOTP_SETUP,
            STEP_TOTP_CODE,
        };
    },
    computed: {
        error() {
            return this.$store.state.user.error;
        },
        infoMsg() {
            return (globalParam('login_page.info_html') || '').trim();
        },
    },
    created() {
        this.customer = customer();
        this.logo = logo();
        this.signupUrl = signupUrl();
        console.log(this.signupUrl);
        this.loginFooterHtml = loginFooterHtml();
    },
    methods: {
        ...mapActions('user', ['loginTry']),
        ...mapActions(['redirectAfterLogin']),
        async login() {
            try {
                this.loading = true;
                let {
                    user,
                    pwChange,
                    totpSetup: needsTotpSetup,
                    totpRequired,
                } = await this.loginTry({ username: this.username, password: this.password });
                if (pwChange) {
                    this.loading = false;
                    this.oldPw = '';
                    this.newPw = '';
                    this.newPwConfirm = '';
                    this.pwChangeError = null;
                    this.showPwChangeDialog = true;
                } else if (needsTotpSetup) {
                    this.totpError = null;
                    this.step = this.STEP_TOTP_SETUP;
                    await this.loadTotpSetup();
                } else if (totpRequired) {
                    this.loading = false;
                    this.totpError = null;
                    this.step = this.STEP_TOTP_CODE;
                } else if (user) {
                    // Only users on the totp exception list get here directly.
                    this.processSuccessfulLogin();
                } else {
                    this.loading = false;
                }
            } catch (e) {
                this.loading = false;
                this.error = e;
            }
        },

        /**
         * Fetches the QR code and the secret for a user without a second factor.
         * Nothing is stored on the user record yet - that happens when the first code
         * is confirmed.
         */
        async loadTotpSetup() {
            this.loading = true;
            try {
                const res = await totpSetup();
                if (res.login_restart) {
                    this.backToCredentials(this.$t('totp_login_restart'));
                    return;
                }
                this.totpQrCode = res.qr_code || '';
                this.totpSecret = res.secret || '';
            } catch (e) {
                this.backToCredentials(errToStr(e));
            } finally {
                this.loading = false;
            }
        },

        /**
         * Sends the code of the second stage. A correct code completes the login, a wrong
         * one leaves the user on this step until the tries are used up.
         */
        async onTotpVerify(code) {
            this.loading = true;
            this.totpError = null;
            try {
                const res = await totpVerify(code);
                if (res.user) {
                    this.processSuccessfulLogin();
                    return;
                }
                if (res.login_restart) {
                    // A locked account ends the pending state too: back to the
                    // credentials form, with the lock message and the end of the lock.
                    if (res.locked) {
                        const until = dayjs(res.locked_until).format('HH:mm');
                        this.backToCredentials(String(this.$t('login_locked', { until })));
                        return;
                    }
                    this.backToCredentials(this.$t('totp_login_restart'));
                    return;
                }
                if (res.totp_invalid_code) {
                    this.totpError = String(this.$t('totp_invalid_code', { count: res.tries_left ?? 0 }));
                    this.$refs.totpCode && this.$refs.totpCode.reset();
                }
            } catch (e) {
                this.totpError = errToStr(e);
            } finally {
                this.loading = false;
            }
        },

        /**
         * Back to step 1. The pending state on the server either expired or was thrown
         * away, so the user has to enter username and password again.
         */
        backToCredentials(message = null) {
            this.step = this.STEP_CREDENTIALS;
            this.password = null;
            this.totpQrCode = '';
            this.totpSecret = '';
            this.totpError = null;
            this.loading = false;
            this.$store.commit('user/setError', message ? String(message) : null);
        },

        processSuccessfulLogin() {
            // Clear user's session storage before proceeding:
            this.clearAllSessionStorage();

            let redirect = appUrl('/');

            // use a ?redirect= url param, if given:
            if (window.location.search) {
                let params = qs.parse(window.location.search, { ignoreQueryPrefix: true });
                if (params?.redirect) {
                    redirect = decodeURIComponent(params.redirect);
                }
            }
            document.location.href = redirect;
        },

        getPWFieldRules() {
            return [formRules.minLength(8)];
        },

        getPWConfirmFieldRules() {
            return [formRules.minLength(8), () => this.newPw === this.newPwConfirm || this.$t('pw_not_match')];
        },

        async onChangePwBtn() {
            try {
                this.loading = true;
                await api().post('/LoginLogout/changePassword', {
                    username: this.username,
                    old_password: this.oldPw,
                    new_password: this.newPw,
                });
                this.password = this.newPw;
                // The dialog must go before the second login: that login no longer always
                // navigates away - it can also switch to a TOTP step, which this persistent
                // dialog would cover. login() opens it again if a password change is still
                // demanded, and a failed login shows its error on the form below.
                this.showPwChangeDialog = false;
                // Now, process the login again with the new password:
                await this.login();
            } catch (e) {
                // Only the changePassword call can land here, and it fails before the
                // dialog is closed - so the message is shown in the still open dialog.
                this.loading = false;
                this.pwChangeError = errToStr(e);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
$breakpoint: 710px;
$formWidth: 350px;
.main-container {
    height: 100vh;
    display: grid;
    background-color: white;
    grid-template-rows: 50px 1fr;
    grid-template-columns: auto 0;
    grid-template-areas:
        'toolbar'
        'login-form';

    @media screen and (min-width: $breakpoint) {
        grid-template-columns:
            max(0, calc(($breakpoint - $formWidth) / 2)) 350px max(0, calc(($breakpoint - $formWidth) / 2))
            1fr;
        grid-template-areas:
            'toolbar toolbar toolbar toolbar'
            '. login-form . image-area';
    }
}

.toolbar {
    padding: 5px;
    display: flex;
    align-items: center;
    grid-area: toolbar;
    background-color: var(--toolbar-bg-color);

    .logo {
        margin-left: 15px;
    }
}

.login-form {
    grid-area: login-form;
    background-color: white;
    display: flex;
    flex-direction: column;

    .inner-container {
        margin-top: 175px;
        padding-left: 20px;
        padding-right: 20px;
        width: 100%;
    }

    // the setup step carries a QR code and explanation text, so it starts further up:
    .inner-container.totp-container {
        margin-top: 60px;
    }

    .footer {
        width: 100%;
        padding-left: 20px;
        padding-right: 20px;
        margin-bottom: 70px;
        font-size: 0.825rem;
    }
}

.image-area {
    display: none;
    grid-area: image-area;
    @media screen and (min-width: $breakpoint) {
        display: block;
    }
}

.info-area {
    margin-left: -40px;
    margin-right: -40px;
    .info-text {
        :deep(p) {
            line-height: 1.5em;
        }
    }
}

.image-area {
    background-size: cover;
    background-repeat: no-repeat;
}

// customer css in customer_specific folder
</style>
